<template>
  <b-modal
    header-close-content=""
    :title="$t('translation.table')"
    v-model="_showTranslationTable"
    @ok="SaveTranslations()"
    size="xl"
    @close="closeModal"
    @cancel="closeModal"
    @show="loadTranslationTable"
    ref="modal"
    no-close-on-backdrop
    no-close-on-esc>
    <div class="fast-page-translation-wrapper">
      <div class="manipulation-area">
        <div class="button-area">
          <b-btn class="btn-with-icon mono" @click="addNew()" :disabled="hasValidationErrors"> <i class="mdp-Plus mr-1"></i>{{ $t("add") }}</b-btn>
        </div>
        <div class="search-area mr-1">
          <div class="filter-search input-group">
            <div class="input-group-prepend" :class="{ isFocused: vm.inputIsFocused }">
              <div class="input-group-text">
                <i class="mdp-Filter"></i>
              </div>
            </div>
            <b-input @focus="vm.inputIsFocused = true" @blur="vm.inputIsFocused = false" :placeholder="$t('Filter')" v-model="vm.searchTerm" />
            <div class="input-group-append" v-if="vm.searchTerm" :class="{ isFocused: vm.inputIsFocused }">
              <b-button @click="emptySearchTerm">
                <i class="mdp-Close"></i>
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <div class="translation-table-wrapper mt-2">
        <table class="table translation-table">
          <thead>
            <tr class="custom-table-tr">
              <th class="width-auto options-column"></th>
              <th class="custom-table-header">
                <span class="custom-table-header-font my-2"> {{ $t("translation.translationKey") }}</span>
              </th>
              <th v-for="lang in languages" :key="`${lang.languageCode}-value`">
                <span>{{ $t("translation.translation") }}</span>
                <span class="language-indication" v-if="languages.length > 1"> ({{ lang.languageCode }})</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              ref="tableTranslation"
              v-for="(item, index) in sortedList"
              :key="index"
              :class="{ selected: index === vm.selectedRow }"
              @click="selectRow(index)">
              <td class="options-column width-auto">
                <b-dropdown no-caret variant="primary" class="table-option" toggle-class="popup-menu-button" menu-class="popup-menu" @toggle="selectRow(index)">
                  <template #button-content><i class="mdp-Ellipsis"></i></template>
                  <b-dropdown-item @click="deleteItem(index)" href="#" link-class="menu-option first-option">
                    <span class="menu-icon"><i class="mdp-Delete"></i></span>
                    <span class="menu-text">{{ $t("contextMenu.delete") }}</span>
                  </b-dropdown-item>
                </b-dropdown>
              </td>
              <td>
                <input
                  type="text"
                  class="form-control"
                  v-if="item[primaryLanguage.languageCode]"
                  v-model="item[primaryLanguage.languageCode].key"
                  @input="syncAcrossLanguages(item, item[primaryLanguage.languageCode].key)"
                  :maxlength="256"
                  :class="{
                    'border-danger':
                      feedbacks[index][primaryLanguage.languageCode].key &&
                      !feedbacks[index][primaryLanguage.languageCode].key.isValid &&
                      feedbacks[index][primaryLanguage.languageCode].key.type == 'error'
                  }"
                  v-tooltip="
                    feedbacks[index][primaryLanguage.languageCode].key &&
                    !feedbacks[index][primaryLanguage.languageCode].key.isValid &&
                    feedbacks[index][primaryLanguage.languageCode].key.feedback
                  " />
              </td>
              <td v-for="lang in languages" :key="`${lang.languageCode}-${index}-value`">
                <text-box-with-fallback
                  :maxLength="1024"
                  v-if="item && item[lang.languageCode] != null"
                  :source="item"
                  field="value"
                  v-model="item[lang.languageCode].value"
                  :language="lang"
                  :validation-data="feedbacks[index][lang.languageCode].value"></text-box-with-fallback>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-overlay z-index="20" opacity="0.4" no-wrap :show="vm.loading">
        <template #overlay>
          <spinner data-cy="manage-catalog-modal-spinner" />
        </template>
      </b-overlay>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <b-btn @click="ok" variant="primary" :disabled="hasValidationErrors || !vm.dirty">{{ $t("OK") }}</b-btn>
      <b-btn @click="cancel">{{ $t("Cancel") }}</b-btn>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import { ILocalizedRecord } from "@medapro-types/common/components/localizedDataListEditorTypes";
import { ITranslationTableTypeModel } from "@medapro-types/Exports/components/FastPage/ITranslationTableTypeModel";
import { reactive, computed, nextTick, ref, watch } from "vue";
import exportApi from "@services/exports";
import { useI18n } from "vue-i18n";
import { MetaDataState } from "@medapro-types/common/metadata";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import TextBoxWithFallback from "@ContentEditor/components/TextBoxWithFallback.vue";
import ruleEvaluator from "@AttributeManager/validations/ruleEvaluator.js";
import _ from "lodash";

const props = defineProps<{ showTranslationTable?: boolean }>();
const { t: $t } = useI18n();
const emit = defineEmits(["update:showTranslationTable"]);
const tableTranslation = ref<HTMLTableRowElement[]>([]);

//#region ViewModel
type ViewModel = {
  translations: ILocalizedRecord<ITranslationTableTypeModel>[];
  selectedRow: number | null;
  inputIsFocused: boolean;
  searchTerm: string;
  loading: boolean;
  dirty: boolean;
  rowsCount: number;
};

const vm = reactive<ViewModel>({
  translations: [] as ILocalizedRecord<ITranslationTableTypeModel>[],
  selectedRow: null,
  inputIsFocused: false,
  searchTerm: "",
  loading: false,
  dirty: false,
  rowsCount: 0
});

const _showTranslationTable = computed({
  get: () => {
    return props.showTranslationTable;
  },
  set: (nv) => {
    emit("update:showTranslationTable", nv);
  }
});

const closeModal = () => {
  emptySearchTerm();
  emit("update:showTranslationTable", false);
};

const sortedList = computed(() => {
  const filter = vm.searchTerm ? vm.searchTerm.toLowerCase() : null;
  return !filter ? vm.translations : vm.translations.filter((x) => x[primaryLanguage.value.languageCode].key.toLocaleLowerCase().includes(filter));
});

const emptySearchTerm = () => {
  vm.searchTerm = "";
  nextTick(() => {
    if (vm.rowsCount > vm.translations.length) {
      scrollToLastElement();
    }
  });
};

const selectRow = (index: number) => {
  if (vm.selectedRow != index) vm.selectedRow = index;
};

const deleteItem = (index: number) => {
  vm.translations.splice(index, 1);
};

const { useState: useMetaDataState } = createNamespacedHelpers<MetaDataState>("metadata");
const { contentLanguages } = useMetaDataState(["contentLanguages"]);

//#region Language
const languages = computed(() => {
  return contentLanguages.value.enabled;
});

const primaryLanguage = computed(() => {
  return contentLanguages.value.enabled[0];
});

const translatedLanguageCodes = computed((): string[] => {
  const translatedLangCodes = _.tail(contentLanguages.value.enabled);
  return translatedLangCodes.map((x) => x.languageCode);
});

//#endregion

async function loadTranslationTable() {
  try {
    vm.loading = true;
    const enabledLanguageCodes = contentLanguages.value.enabled.map((x) => x.languageCode);
    const data = await exportApi.getTranslationTable(enabledLanguageCodes);
    vm.translations = data;
    vm.rowsCount = vm.translations.length;
  } catch (error) {
    console.error("Error on loading translation table:", error);
  } finally {
    vm.loading = false;
  }
}

async function SaveTranslations() {
  try {
    const enabledLanguageCodes = contentLanguages.value.enabled.map((x) => x.languageCode);
    const strippedModels = vm.translations.map((x) => _.pick(x, enabledLanguageCodes));
    await exportApi.updateTranslationTable(strippedModels);
  } catch (error) {
    console.error(error);
  }
}

function addNew() {
  const row: ILocalizedRecord<ITranslationTableTypeModel> = Object.fromEntries(
    contentLanguages.value.enabled.map((x) => [
      x.languageCode,
      {
        key: generateNewName($t("newValues.TranslationKey"), vm.translations),
        value: "",
        languageName: x.languageCode
      }
    ])
  );

  vm.translations.push(row);

  if (vm.searchTerm) {
    vm.rowsCount = vm.translations.length + 1;
  }
  nextTick(() => {
    scrollToLastElement();
  });
}

async function scrollToLastElement() {
  const index = tableTranslation.value.length - 1;
  const tableRow = tableTranslation.value[index];
  selectRow(index);

  tableRow?.scrollIntoView({ behavior: "smooth" });
  const inputs = tableRow?.getElementsByTagName("input");
  if (inputs && inputs.length > 0) {
    inputs[0].focus();
  }
}

function generateNewName(nameTemplate: string, values: ILocalizedRecord<ITranslationTableTypeModel>[]) {
  if (values.every((x) => x[primaryLanguage.value.languageCode].key !== nameTemplate)) {
    return nameTemplate;
  }

  const pattern = new RegExp(nameTemplate + " \\((?<counter>\\d+)\\)");
  const counters = values
    .map((x) => pattern.exec(x[primaryLanguage.value.languageCode].key)?.groups?.counter)
    .filter((x) => x)
    .map((x) => parseInt(x))
    .sort((a, b) => a - b);
  const nextCounter = counters.length ? counters[counters.length - 1] + 1 : 1;
  return `${nameTemplate} (${nextCounter})`;
}

function syncAcrossLanguages(source: ILocalizedRecord<ITranslationTableTypeModel>, value: string) {
  if (!translatedLanguageCodes.value || translatedLanguageCodes.value.length === 0) return;
  nextTick(() => {
    translatedLanguageCodes.value.forEach((x) => (source[x].key = value));
  });
}

const feedbacks: Record<string, any> = computed(() => {
  const rules = {
    key: [
      {
        rule: (entry: ITranslationTableTypeModel) => !entry.key || entry.key.length <= 256, //either empty or less than 256 chars
        msg: $t("validation.lengthExceeds", { length: 256, field: $t("translation.translationKey") }),
        severity: "error"
      },
      {
        rule: (entry: ITranslationTableTypeModel) => entry.key,
        msg: $t("validation.valueRequired", { field: $t("translation.translationKey") }),
        severity: "error"
      },
      {
        rule: (entry: ITranslationTableTypeModel, lang: string) => vm.translations.filter((t) => t[lang]?.key && t[lang]?.key === entry?.key)?.length <= 1, //empty or does not match any other same language entry
        msg: $t("validation.duplicateExists", { field: $t("translation.translationKey") }),
        severity: "error"
      }
    ],
    value: [
      {
        rule: (entry: ITranslationTableTypeModel) => !entry.value || entry.value.length <= 1024,
        msg: $t("validation.lengthExceeds", { length: 1024, field: $t("translation.translationKey") }),
        severity: "error"
      },
      {
        rule: (entry: ITranslationTableTypeModel, lang: string, row: ILocalizedRecord<ITranslationTableTypeModel>) =>
          languages.value.some((lang) => row[lang.languageCode]?.value !== "" && row[lang.languageCode]?.value !== null),
        msg: $t("validation.requiredOneValue", { field: $t("translation.translationKey") }),
        severity: "error"
      }
    ]
  };

  const feedbacks = vm.translations.map((row) => ruleEvaluator(row, rules, languages.value));
  return feedbacks;
});

const hasValidationErrors = computed(() => {
  if (
    feedbacks.value
      .flatMap((row: ILocalizedRecord<ITranslationTableTypeModel>) => languages.value.map((lang) => row[lang.languageCode]))
      .some((row: Record<string, string>) => Object.keys(row).some((field: string) => !row[field].isValid && row[field].type == "error"))
  )
    return true;

  return false;
});

watch(
  () => vm.translations,
  (nv, ov) => {
    vm.dirty = nv === ov;
  },
  { deep: true }
);
</script>
